import React, { useState } from "react";
import { Helmet } from "react-helmet";
import {
  FiArrowRight,
  FiCheckCircle,
  FiEye,
  FiEyeOff,
  FiLock,
  FiMail,
  FiShield,
  FiAlertCircle,
} from "react-icons/fi";

import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import "./Login.css";

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setLoginError(false);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = "Email address is required.";
    } else if (
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    ) {
      newErrors.email = "Please enter a valid email address.";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required.";
    } else if (formData.password.length < 6) {
      newErrors.password =
        "Password must contain at least 6 characters.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const isValid = validateForm();

    if (!isValid) {
      return;
    }

    /*
      Authentication is not connected yet.

      For now, show the requested error state
      after valid details are submitted.
    */

    setLoginError(true);
  };

  return (
    <>
      <Helmet>
        <title>Login | Dropgear IO</title>

        <meta
          name="description"
          content="Sign in to your Dropgear IO account."
        />
      </Helmet>

      <Header />

      <main className="dg-login-page">

        {/* =====================================================
            LOGIN HERO
        ===================================================== */}

        <section className="dg-login-section">

          <div className="dg-login-container">

            <div className="dg-login-layout">

              {/* =================================================
                  LEFT SIDE
              ================================================= */}

              <div className="dg-login-intro">

                <span className="dg-login-eyebrow">
                  DROPGEAR IO
                </span>

                <h1>
                  Welcome
                  <span> back.</span>
                </h1>

                <p>
                  Sign in to continue working with your AI-powered
                  workflows, automation systems, and intelligent
                  business tools.
                </p>


                {/* BENEFITS */}

                <div className="dg-login-features">

                  <div className="dg-login-feature">

                    <span className="dg-login-feature-icon">
                      <FiShield />
                    </span>

                    <div>
                      <strong>
                        Intelligent Workflows
                      </strong>

                      <p>
                        Manage your connected AI workflows from one place.
                      </p>
                    </div>

                  </div>


                  <div className="dg-login-feature">

                    <span className="dg-login-feature-icon">
                      <FiCheckCircle />
                    </span>

                    <div>
                      <strong>
                        Built for Business
                      </strong>

                      <p>
                        Access tools designed around practical business
                        workflows.
                      </p>
                    </div>

                  </div>


                  <div className="dg-login-feature">

                    <span className="dg-login-feature-icon">
                      <FiLock />
                    </span>

                    <div>
                      <strong>
                        Your Workspace
                      </strong>

                      <p>
                        Continue from where your team left off.
                      </p>
                    </div>

                  </div>

                </div>

              </div>


              {/* =================================================
                  LOGIN CARD
              ================================================= */}

              <div className="dg-login-card">

                <div className="dg-login-card-header">

                  <div className="dg-login-lock-icon">
                    <FiLock />
                  </div>

                  <span>
                    ACCOUNT LOGIN
                  </span>

                  <h2>
                    Sign in to Dropgear IO
                  </h2>

                  <p>
                    Enter your account details to continue.
                  </p>

                </div>


                {/* =================================================
                    ERROR MESSAGE
                ================================================= */}

                {loginError && (

                  <div className="dg-login-error-message">

                    <FiAlertCircle />

                    <div>
                      <strong>
                        Something Went Wrong
                      </strong>

                      <p>
                        Please try again.
                      </p>
                    </div>

                  </div>

                )}


                {/* =================================================
                    FORM
                ================================================= */}

                <form
                  className="dg-login-form"
                  onSubmit={handleSubmit}
                  noValidate
                >

                  {/* EMAIL */}

                  <div className="dg-login-field">

                    <label htmlFor="dg-login-email">
                      Email Address
                    </label>

                    <div
                      className={`dg-login-input-wrapper ${
                        errors.email
                          ? "dg-login-input-error"
                          : ""
                      }`}
                    >

                      <FiMail />

                      <input
                        id="dg-login-email"
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@company.com"
                        autoComplete="email"
                      />

                    </div>

                    {errors.email && (
                      <span className="dg-login-validation">
                        {errors.email}
                      </span>
                    )}

                  </div>


                  {/* PASSWORD */}

                  <div className="dg-login-field">

                    <div className="dg-login-label-row">

                      <label htmlFor="dg-login-password">
                        Password
                      </label>

                      {/* <a
                        href="/forgot-password"
                        className="dg-login-forgot"
                      >
                        Forgot password?
                      </a> */}

                    </div>


                    <div
                      className={`dg-login-input-wrapper ${
                        errors.password
                          ? "dg-login-input-error"
                          : ""
                      }`}
                    >

                      <FiLock />

                      <input
                        id="dg-login-password"
                        type={
                          showPassword
                            ? "text"
                            : "password"
                        }
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                        autoComplete="current-password"
                      />

                      <button
                        type="button"
                        className="dg-login-password-toggle"
                        onClick={() =>
                          setShowPassword((prev) => !prev)
                        }
                        aria-label={
                          showPassword
                            ? "Hide password"
                            : "Show password"
                        }
                      >
                        {showPassword ? (
                          <FiEyeOff />
                        ) : (
                          <FiEye />
                        )}
                      </button>

                    </div>

                    {errors.password && (
                      <span className="dg-login-validation">
                        {errors.password}
                      </span>
                    )}

                  </div>


                  {/* REMEMBER */}

                  <label className="dg-login-remember">

                    <input type="checkbox" />

                    <span>
                      Remember me
                    </span>

                  </label>


                  {/* SUBMIT */}

                  <button
                    type="submit"
                    className="dg-login-submit"
                  >

                    <span>
                      Sign In
                    </span>

                    <FiArrowRight />

                  </button>

                </form>


                {/* DIVIDER */}

                <div className="dg-login-divider">

                  <span></span>

                  <p>
                    New to Dropgear IO?
                  </p>

                  <span></span>

                </div>


                {/* DEMO CTA */}

                <a
                  href="/book-demo"
                  className="dg-login-demo"
                >
                  Book a Demo
                  <FiArrowRight />
                </a>


                <p className="dg-login-bottom-text">
                  Don't have an account yet? Start by exploring
                  Dropgear IO with our team.
                </p>

              </div>

            </div>

          </div>

        </section>

      </main>

      <Footer />
    </>
  );
};

export default Login;

